import { generateId } from 'ai';
import { isEmpty, orderBy } from 'lodash';
import { create } from 'zustand';

import type {
  StoredThread,
  StoredMessage,
  StoredAttachment,
  StepTimings,
  ThreadCreateInput,
  ThreadUpdateInput,
  MessageCreateInput,
  MessageUpdateInput,
} from '@/lib/assistant-cloud-local/types';
import { popServerMessageId, setRemoteIdForMessage } from '@/lib/utils/message';
import {
  getOrCreateThreadUuid,
  getPendingLocalThreadId,
  clearPendingLocalThreadId,
} from '@/lib/utils/thread';

import * as db from './indexeddb/db';

function sortMessagesTreeOrder(messages: StoredMessage[]): StoredMessage[] {
  return orderBy(messages, ['height', m => m.created_at.getTime()], ['asc', 'asc']);
}

let cachedMessagesIndexFor: Map<string, StoredMessage[]> | null = null;
let cachedMessagesIndex: Map<string, StoredMessage> | null = null;

// Index rebuilt when the caller passes a new `messages` Map identity — store
// mutations always spread into a new Map.
export function getMessageById(
  messages: Map<string, StoredMessage[]>,
  messageId: string,
): StoredMessage | undefined {
  if (cachedMessagesIndexFor !== messages || !cachedMessagesIndex) {
    const index = new Map<string, StoredMessage>();
    for (const arr of messages.values()) {
      for (const m of arr) index.set(m.id, m);
    }
    cachedMessagesIndexFor = messages;
    cachedMessagesIndex = index;
  }
  return cachedMessagesIndex.get(messageId);
}

function resetMessagesIndex(): void {
  cachedMessagesIndexFor = null;
  cachedMessagesIndex = null;
}

// ---------------------------------------------------------------------------
// Pending-timing side-channel
//
// The transport observes stream chunks and computes `step_timings` +
// `stream_duration_ms`, but it has no handle to the persisted message — the
// store generates that id later. So the transport stages the data here, keyed
// by `threadId`, and the next persistence write on that thread drains it.
//
// Two assistant-ui flow patterns must work:
//
//   Pattern A — "createMessage early, updateMessage fills":
//     • `createMessage(assistant)` runs at stream-START with empty content.
//       Pending is empty (transport hasn't pushed yet) — nothing stamped.
//     • Stream finalizes → `pushPendingTiming` populates the entry.
//     • `updateMessage(assistant)` runs with the streamed content → drains
//       the pending and stamps the existing record.
//
//   Pattern B — "createMessage after finalize" (this app's actual flow):
//     • Stream finalizes → `pushPendingTiming` populates the entry.
//     • `createMessage(assistant)` runs with the full content → drains the
//       pending and stamps the new record.
//     • Subsequent `updateMessage` calls — including the ones assistant-ui's
//       `CloudMessagePersistence` issues on PREVIOUS assistant messages when
//       a new stream wraps up (state/finishedAt refresh) — must NOT consume.
//       Otherwise the new stream's pending gets drained into the wrong
//       (older) record and the new record never gets stamped.
//
// The compatibility hinge is in `updateMessage`: skip consume when the
// existing record already has `step_timings` / `stream_duration_ms`. Pattern
// A messages haven't been stamped yet → consume fires; pattern B messages
// (including the older-message refreshes) are already stamped → consume is
// skipped. See the inline comment at the call site for the full rationale.
//
// Repeated calls merge into the same pending entry. The 60 s TTL evicts
// orphans if a stream errors before any persistence write drains it.
// ---------------------------------------------------------------------------
const PENDING_TIMING_TTL_MS = 60_000;
type PendingTiming = { durationMs?: number; timings?: StepTimings; createdAt?: number };
const pendingTimings = new Map<string, PendingTiming>();
const pendingTimingEvictionTimers = new Map<string, ReturnType<typeof setTimeout>>();

function scheduleEviction(threadId: string): void {
  const existing = pendingTimingEvictionTimers.get(threadId);
  if (existing) clearTimeout(existing);
  const timer = setTimeout(() => {
    pendingTimings.delete(threadId);
    pendingTimingEvictionTimers.delete(threadId);
  }, PENDING_TIMING_TTL_MS);
  pendingTimingEvictionTimers.set(threadId, timer);
}

/**
 * Stages timing data for the next `createMessage` / `updateMessage` on the
 * thread. Any subset of fields can be passed — repeated calls merge into the
 * same pending entry, keyed by `threadId`. Each field is validated
 * independently and silently dropped when invalid:
 *
 *   - `durationMs`: must be finite and >= 0 (0 is valid for cached responses;
 *     negatives indicate clock skew).
 *   - `timings`: must contain at least one step.
 *   - `createdAt`: must be a finite epoch ms. Used by the storybook mock
 *     transport whose synthetic clock is detached from `Date.now()`;
 *     production omits it and relies on the default backdate-from-`durationMs`.
 */
export function pushPendingTiming({
  threadId,
  durationMs,
  timings,
  createdAt,
}: {
  threadId: string;
  durationMs?: number;
  timings?: StepTimings;
  createdAt?: number;
}): void {
  const patch: PendingTiming = {};
  if (durationMs !== undefined && Number.isFinite(durationMs) && durationMs >= 0) {
    patch.durationMs = durationMs;
  }
  if (timings !== undefined && !isEmpty(timings)) {
    patch.timings = timings;
  }
  if (createdAt !== undefined && Number.isFinite(createdAt)) {
    patch.createdAt = createdAt;
  }
  if (isEmpty(patch)) return;
  const current = pendingTimings.get(threadId) ?? {};
  pendingTimings.set(threadId, { ...current, ...patch });
  scheduleEviction(threadId);
}

/**
 * Drains the pending timing entry for `threadId`, returning whatever was
 * staged by `pushPendingTiming`. Single-shot — repeated calls return
 * `undefined` until the next push. Both `createMessage` and `updateMessage`
 * call this for assistant-role messages; only one of them will get a
 * non-empty result per stream (see the module-level docblock for which one
 * fires under each flow pattern).
 *
 * Note: we deliberately do NOT clear the live-trail mirror here. At the
 * running → finished transition, the runtime's message id can briefly lag
 * the persisted id, and clearing would force the trail to fall back to
 * assistant-ui's later `message.createdAt` and shift every row. The
 * persisted-wins merge in `useMergedStepTimings` makes the lingering
 * live-mirror entry harmless; the live-store's own TTL evicts it.
 */
/**
 * A reply cannot predate the question. The backdate below measures the stream from when the request
 * left, which is at or before the moment the user's own message was stamped — so an assistant
 * message could be persisted with a `created_at` a few hundred milliseconds *before* the message it
 * answers, and the thread rendered the reply above the question.
 *
 * Bounded on both sides: never before its parent, and never after `now`, which is its own
 * `updated_at`. Without the upper bound a reply that finished inside the same millisecond as the
 * question would be pushed past its own end and report a negative duration. In the ordinary case —
 * a stream measured in hundreds of milliseconds — neither bound is reached and the duration stands.
 */
function withinParentAndNow({
  createdAt,
  parent,
  now,
}: {
  createdAt: Date;
  parent: StoredMessage | null | undefined;
  now: Date;
}): Date {
  if (!parent) return createdAt;
  const floor = parent.created_at.getTime() + 1;
  const bounded = Math.min(Math.max(createdAt.getTime(), floor), now.getTime());
  return bounded === createdAt.getTime() ? createdAt : new Date(bounded);
}

function consumePendingTiming(threadId: string): PendingTiming | undefined {
  const v = pendingTimings.get(threadId);
  if (v === undefined) return undefined;
  pendingTimings.delete(threadId);
  const timer = pendingTimingEvictionTimers.get(threadId);
  if (timer) {
    clearTimeout(timer);
    pendingTimingEvictionTimers.delete(threadId);
  }
  return v;
}

/**
 * Drops every pending timing entry and cancels its eviction timer. Used by
 * `clear()` so test fixtures don't leak setTimeouts across describe blocks
 * under `jest.useFakeTimers()`, and so a logout doesn't leave timings primed
 * for the next user's first stream.
 */
function clearPendingTimings(): void {
  for (const timer of pendingTimingEvictionTimers.values()) clearTimeout(timer);
  pendingTimingEvictionTimers.clear();
  pendingTimings.clear();
}

function warnFiltered({ reason, ids }: { reason: string; ids: string[] }): void {
  if (ids.length === 0) return;
  console.warn(`[assistant-cloud-store] Filtered ${reason}:`, { count: ids.length, ids });
}

/**
 * Drops messages without ids, duplicate ids, orphans (parent_id → missing
 * message), and parent cycles. Any of these reaching MessageRepository.import
 * throws ("same id already exists in the parent tree" / sparse-array errors), so
 * this is the defensive boundary that keeps a corrupt stored thread from crashing
 * the runtime on load.
 */
export function filterValidMessageChain(messages: StoredMessage[]): StoredMessage[] {
  if (messages.length === 0) return messages;

  // De-duplicate by id (keep first occurrence). A duplicate id imported twice with
  // a different parent makes MessageRepository link a node under its own ancestor.
  const byId = new Map<string, StoredMessage>();
  const duplicateIds: string[] = [];
  for (const message of messages) {
    if (!message.id) {
      console.warn('[assistant-cloud-store] Filtering message without ID:', {
        format: message.format,
      });
      continue;
    }
    if (byId.has(message.id)) {
      duplicateIds.push(message.id);
      continue;
    }
    byId.set(message.id, message);
  }

  const validMessages: StoredMessage[] = [];
  const orphanedIds: string[] = [];
  const cyclicIds: string[] = [];

  for (const message of byId.values()) {
    if (message.parent_id === null) {
      validMessages.push(message);
      continue;
    }

    // Walk the parent chain: it must terminate at a root without revisiting this
    // message (a cycle would make import build a node that is its own ancestor).
    const seen = new Set<string>([message.id]);
    let cyclic = false;
    let ancestor = byId.get(message.parent_id);
    while (ancestor) {
      if (seen.has(ancestor.id)) {
        cyclic = true;
        break;
      }
      seen.add(ancestor.id);
      if (ancestor.parent_id === null) break;
      ancestor = byId.get(ancestor.parent_id);
    }

    if (cyclic) {
      cyclicIds.push(message.id);
    } else if (byId.has(message.parent_id)) {
      validMessages.push(message);
    } else {
      orphanedIds.push(message.id);
    }
  }

  warnFiltered({ reason: 'duplicate-id messages', ids: duplicateIds });
  warnFiltered({ reason: 'orphaned messages', ids: orphanedIds });
  warnFiltered({ reason: 'cyclic messages', ids: cyclicIds });

  return validMessages;
}

function sortThreadsByUpdatedAt(threads: StoredThread[]): StoredThread[] {
  return threads.sort((a, b) => {
    const dateA = a.updated_at instanceof Date ? a.updated_at : new Date(a.updated_at);
    const dateB = b.updated_at instanceof Date ? b.updated_at : new Date(b.updated_at);
    return dateB.getTime() - dateA.getTime();
  });
}

function sortedThreadsToMap(threads: StoredThread[]): Map<string, StoredThread> {
  return new Map(threads.map(t => [t.id, t]));
}

interface AssistantCloudState {
  threads: Map<string, StoredThread>;
  messages: Map<string, StoredMessage[]>;
  attachments: Map<string, StoredAttachment>;
  isInitialized: boolean;
  isLoading: boolean;
  isSyncing: boolean;
  initPromise: Promise<void> | null;

  initialize: () => Promise<void>;
  createThread: (data: ThreadCreateInput) => Promise<string>;
  getThread: (id: string) => StoredThread | undefined;
  getSortedThreads: (options?: { archived?: boolean }) => StoredThread[];
  updateThread: (id: string, updates: ThreadUpdateInput) => Promise<void>;
  deleteThread: (id: string) => Promise<void>;
  listThreads: (options?: { archived?: boolean }) => Promise<StoredThread[]>;
  createMessage: (threadId: string, data: MessageCreateInput) => Promise<string>;
  updateMessage: (threadId: string, messageId: string, data: MessageUpdateInput) => Promise<void>;
  getMessage: (id: string) => StoredMessage | undefined;
  listMessages: (threadId: string) => Promise<StoredMessage[]>;
  uploadAttachment: (file: File) => Promise<string>;
  getAttachment: (id: string) => StoredAttachment | undefined;
  clear: () => Promise<void>;
  setSyncing: (syncing: boolean) => void;
}

export const useAssistantCloudStore = create<AssistantCloudState>()((set, get) => ({
  threads: new Map(),
  messages: new Map(),
  attachments: new Map(),
  isInitialized: false,
  isLoading: false,
  isSyncing: false,
  initPromise: null,

  initialize: async () => {
    const { initPromise, isInitialized } = get();
    if (initPromise) {
      return initPromise;
    }
    if (isInitialized) {
      return Promise.resolve();
    }

    const promise = (async () => {
      set({ isLoading: true });

      try {
        const threads = await db.listThreads({ archived: false });
        const sortedThreads = sortThreadsByUpdatedAt(threads);
        const threadsMap = sortedThreadsToMap(sortedThreads);

        const messagesMap = new Map<string, StoredMessage[]>();
        for (const thread of sortedThreads) {
          const messages = await db.listMessages(thread.id);
          const validMessages = filterValidMessageChain(messages);
          messagesMap.set(thread.id, sortMessagesTreeOrder(validMessages));
        }

        const attachments = await db.listAttachments();
        const attachmentsMap = new Map(attachments.map(a => [a.id, a]));

        set({
          threads: threadsMap,
          messages: messagesMap,
          attachments: attachmentsMap,
          isInitialized: true,
          isLoading: false,
          initPromise: null,
        });
      } catch (error) {
        console.error('Failed to initialize store:', error);
        set({ isLoading: false, initPromise: null });
        throw error;
      }
    })();

    set({ initPromise: promise });
    return promise;
  },

  createThread: async data => {
    const pendingThreadId = getPendingLocalThreadId();
    const id = pendingThreadId ? getOrCreateThreadUuid(pendingThreadId) : generateId();

    const now = new Date();

    const thread: StoredThread = {
      id,
      external_id: data.external_id ?? null,
      title: data.title || '',
      is_archived: false,
      created_at: now,
      updated_at: now,
      last_message_at: data.last_message_at || now,
    };

    set({ isSyncing: true });

    try {
      await db.createThread(thread);

      set(state => {
        const threadsArray = [thread, ...Array.from(state.threads.values())];
        const sortedThreads = sortThreadsByUpdatedAt(threadsArray);

        return {
          threads: sortedThreadsToMap(sortedThreads),
          messages: new Map(state.messages).set(id, []),
          isSyncing: false,
        };
      });

      clearPendingLocalThreadId();

      return id;
    } catch (error) {
      set({ isSyncing: false });
      throw error;
    }
  },

  getThread: id => get().threads.get(id),

  getSortedThreads: options => {
    const { threads } = get();
    const threadsArray = Array.from(threads.values());
    const filtered =
      options?.archived !== undefined
        ? threadsArray.filter(t => t.is_archived === options.archived)
        : threadsArray;
    return sortThreadsByUpdatedAt([...filtered]);
  },

  updateThread: async (id, updates) => {
    const { threads } = get();
    const thread = threads.get(id);
    if (!thread) {
      throw new Error(`Thread not found: ${id}`);
    }

    set({ isSyncing: true });

    try {
      await db.updateThread(id, updates);

      set(state => {
        const updatedThread = { ...thread, ...updates, updated_at: new Date() };
        const threadsArray = Array.from(state.threads.values()).map(t =>
          t.id === id ? updatedThread : t,
        );
        const sortedThreads = sortThreadsByUpdatedAt(threadsArray);

        return {
          threads: sortedThreadsToMap(sortedThreads),
          isSyncing: false,
        };
      });
    } catch (error) {
      set({ isSyncing: false });
      throw error;
    }
  },

  deleteThread: async id => {
    set({ isSyncing: true });

    try {
      await db.deleteThread(id);

      set(state => {
        const threads = new Map(state.threads);
        const messages = new Map(state.messages);
        threads.delete(id);
        messages.delete(id);
        return { threads, messages, isSyncing: false };
      });
    } catch (error) {
      set({ isSyncing: false });
      throw error;
    }
  },

  listThreads: async options => {
    const threads = await db.listThreads(options);
    const sortedThreads = sortThreadsByUpdatedAt(threads);

    set({
      threads: sortedThreadsToMap(sortedThreads),
    });

    return threads;
  },

  createMessage: async (threadId, data) => {
    const content = data.content as { role?: string } | undefined;
    const isAssistantMessage = content?.role === 'assistant';
    const serverMessageId = isAssistantMessage ? popServerMessageId(threadId) : null;
    const id = data.id || generateId();
    const now = new Date();
    const { messages } = get();
    const threadMessages = messages.get(threadId) || [];
    const parentMessage = data.parent_id ? threadMessages.find(m => m.id === data.parent_id) : null;
    const height = parentMessage ? parentMessage.height + 1 : 0;

    if (serverMessageId) {
      setRemoteIdForMessage(id, serverMessageId);
    }

    // Backdate so the trail header shows the real stream duration. Transport
    // `flush()` runs before this, so duration + step timings are already stashed.
    let createdAt = now;
    let streamDurationMs: number | undefined;
    let stepTimings: StepTimings | undefined;
    if (isAssistantMessage) {
      const pending = consumePendingTiming(threadId);
      if (pending?.createdAt !== undefined) {
        createdAt = new Date(pending.createdAt);
        streamDurationMs = pending?.durationMs;
      } else if (pending?.durationMs !== undefined) {
        createdAt = new Date(now.getTime() - pending.durationMs);
        streamDurationMs = pending.durationMs;
      }
      stepTimings = pending?.timings;
    }

    const message: StoredMessage = {
      id,
      remote_id: serverMessageId,
      thread_id: threadId,
      parent_id: data.parent_id ?? null,
      height,
      format: data.format,
      content: data.content,
      created_at: withinParentAndNow({ createdAt, parent: parentMessage, now }),
      updated_at: now,
      ...(streamDurationMs !== undefined && { stream_duration_ms: streamDurationMs }),
      ...(stepTimings && { step_timings: stepTimings }),
    };

    // assistant-ui can re-create a message under the same id mid-run (id-swap
    // path, react-ai-sdk #4037). db.createMessage is an IndexedDB `add` that
    // rejects duplicate keys, so a replacement must upsert via `updateMessage`
    // (put) to actually persist — otherwise it silently reverts on reload.
    const isReplacement = threadMessages.some(m => m.id === id);

    set({ isSyncing: true });

    try {
      await (isReplacement ? db.updateMessage(message) : db.createMessage(message));
      await db.updateThread(threadId, { last_message_at: now });

      set(state => {
        const thread = state.threads.get(threadId);
        if (!thread) {
          return { isSyncing: false };
        }

        const updatedThread = {
          ...thread,
          last_message_at: now,
          updated_at: now,
        };

        const threadsArray = Array.from(state.threads.values()).map(t =>
          t.id === threadId ? updatedThread : t,
        );
        const sortedThreads = sortThreadsByUpdatedAt(threadsArray);

        // Derive from current state (not the pre-await snapshot, which a concurrent
        // write could have superseded) and replace any existing entry for this id.
        const current = state.messages.get(threadId) ?? [];
        const existingIndex = current.findIndex(m => m.id === id);
        const nextThreadMessages =
          existingIndex === -1
            ? [...current, message]
            : current.map((m, i) => (i === existingIndex ? message : m));

        return {
          messages: new Map(state.messages).set(threadId, nextThreadMessages),
          threads: sortedThreadsToMap(sortedThreads),
          isSyncing: false,
        };
      });

      return id;
    } catch (error) {
      set({ isSyncing: false });
      throw error;
    }
  },

  updateMessage: async (threadId, messageId, data) => {
    const { messages } = get();
    const threadMessages = messages.get(threadId) || [];
    const existing = threadMessages.find(m => m.id === messageId);
    if (!existing) return;
    const parentOfExisting = existing.parent_id
      ? threadMessages.find(m => m.id === existing.parent_id)
      : null;

    const now = new Date();
    // `alreadyStamped` is the load-bearing guard for the pending-timing
    // side-channel — see the module-level docblock above `pendingTimings`
    // for the full pattern-A vs pattern-B rationale. tl;dr: an
    // already-stamped assistant message is either pattern B's just-created
    // record (already drained pending in createMessage) or a *previous*
    // assistant message that assistant-ui is refreshing because a NEW
    // stream just finalized — consuming here would steal the new stream's
    // pending and stamp it onto the wrong record.
    const isAssistantMessage =
      (existing.content as { role?: string } | undefined)?.role === 'assistant';
    const alreadyStamped =
      existing.step_timings !== undefined || existing.stream_duration_ms !== undefined;
    let createdAtUpdate: Date | undefined;
    let streamDurationMsUpdate: number | undefined;
    let stepTimingsUpdate: StepTimings | undefined;
    if (isAssistantMessage && !alreadyStamped) {
      const pending = consumePendingTiming(threadId);
      if (pending?.createdAt !== undefined) {
        createdAtUpdate = new Date(pending.createdAt);
        streamDurationMsUpdate = pending.durationMs;
      } else if (pending?.durationMs !== undefined) {
        createdAtUpdate = new Date(now.getTime() - pending.durationMs);
        streamDurationMsUpdate = pending.durationMs;
      }
      if (pending?.timings) stepTimingsUpdate = pending.timings;
    }

    const updated: StoredMessage = {
      ...existing,
      ...(data.content !== undefined && { content: data.content }),
      ...(data.format !== undefined && { format: data.format }),
      ...(createdAtUpdate && {
        created_at: withinParentAndNow({
          createdAt: createdAtUpdate,
          parent: parentOfExisting,
          now,
        }),
      }),
      ...(streamDurationMsUpdate !== undefined && { stream_duration_ms: streamDurationMsUpdate }),
      ...(stepTimingsUpdate && { step_timings: stepTimingsUpdate }),
      updated_at: now,
    };

    set({ isSyncing: true });
    try {
      await db.updateMessage(updated);
      set(state => ({
        messages: new Map(state.messages).set(
          threadId,
          (state.messages.get(threadId) || []).map(m => (m.id === messageId ? updated : m)),
        ),
        isSyncing: false,
      }));
    } catch (error) {
      set({ isSyncing: false });
      throw error;
    }
  },

  getMessage: id => getMessageById(get().messages, id),

  // Format filtering happens in MessagesAPI.list() — it normalizes the format
  // so FormattedCloudPersistence can decode regardless of stored format.
  listMessages: async threadId => {
    const { messages } = get();
    const cachedMessages = messages.get(threadId);

    if (cachedMessages?.length) {
      return cachedMessages;
    }

    const allMessages = await db.listMessages(threadId);
    const validMessages = filterValidMessageChain(allMessages);
    const sortedMessages = sortMessagesTreeOrder(validMessages);

    set(state => ({
      messages: new Map(state.messages).set(threadId, sortedMessages),
    }));

    return sortedMessages;
  },

  uploadAttachment: async file => {
    const id = generateId();
    const attachment: StoredAttachment = {
      id,
      name: file.name,
      content_type: file.type,
      size: file.size,
      data: file,
      created_at: new Date(),
    };

    set({ isSyncing: true });

    try {
      await db.createAttachment(attachment);

      set(state => ({
        attachments: new Map(state.attachments).set(id, attachment),
        isSyncing: false,
      }));

      return id;
    } catch (error) {
      set({ isSyncing: false });
      throw error;
    }
  },

  getAttachment: id => get().attachments.get(id),

  clear: async () => {
    set({ isSyncing: true });

    try {
      await db.clearDB();

      resetMessagesIndex();
      clearPendingTimings();
      set({
        threads: new Map(),
        messages: new Map(),
        attachments: new Map(),
        isSyncing: false,
      });
    } catch (error) {
      set({ isSyncing: false });
      throw error;
    }
  },

  setSyncing: syncing => set({ isSyncing: syncing }),
}));
